/**
 * Format a timestamp as relative time (e.g., "30 seconds ago", "2 minutes ago")
 * @param timestamp - Epoch milliseconds
 * @returns Relative time string
 */
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 5) {
    return 'just now';
  } else if (diffSeconds < 60) {
    return `${diffSeconds} second${diffSeconds !== 1 ? 's' : ''} ago`;
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  }
}

// ===== Date Range Types and Utilities =====

/**
 * Represents a date range with start and end dates in YYYY-MM-DD format
 */
export interface DateRange {
  start_date: string;
  end_date: string;
}

/**
 * Preset date range types available in the system
 */
export type RangeType = 
  | 'today' 
  // | 'yesterday'
  | 'last7days' 
  | 'last30days' 
  | 'last60days'
  | 'last90days'
  | 'mtd'
  | 'lastmonth' 
  // | 'qtd'
  | 'ytd'
  | 'custom';

/**
 * Configuration for a preset range
 */
export interface RangePreset {
  type: RangeType;
  label: string;
  shortLabel: string;
  calculate: () => DateRange;
}

/**
 * Format a Date object as YYYY-MM-DD string in UTC
 * @param date - Date to format
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateUTC(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get today's date in UTC as a date range (single day)
 * @returns DateRange for today
 */
export function getToday(): DateRange {
  const now = new Date();
  const today = formatDateUTC(now);
  return {
    start_date: today,
    end_date: today
  };
}

/**
 * Get yesterday's date in UTC as a date range (single day)
 * @returns DateRange for yesterday
 */
export function getYesterday(): DateRange {
  const now = new Date();
  const yesterday = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() - 1
  ));
  const dateStr = formatDateUTC(yesterday);
  return {
    start_date: dateStr,
    end_date: dateStr
  };
}

/**
 * Get the last N days (including today) as a date range
 * @param days - Number of days to include
 * @returns DateRange for the last N days
 */
export function getLastNDays(days: number): DateRange {
  const now = new Date();
  const endDate = formatDateUTC(now);
  
  const startDate = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() - (days - 1)
  ));
  
  return {
    start_date: formatDateUTC(startDate),
    end_date: endDate
  };
}

/**
 * Get the last 7 days (including today) as a date range
 * @returns DateRange for the last 7 days
 */
export function getLast7Days(): DateRange {
  return getLastNDays(7);
}

/**
 * Get the last 30 days (including today) as a date range
 * @returns DateRange for the last 30 days
 */
export function getLast30Days(): DateRange {
  return getLastNDays(30);
}

/**
 * Get the last 60 days (including today) as a date range
 * @returns DateRange for the last 60 days
 */
export function getLast60Days(): DateRange {
  return getLastNDays(60);
}

/**
 * Get the last 90 days (including today) as a date range
 * @returns DateRange for the last 90 days
 */
export function getLast90Days(): DateRange {
  return getLastNDays(90);
}

/**
 * Get Month-to-Date range (first day of current month through today)
 * @returns DateRange for month-to-date
 */
export function getMonthToDate(): DateRange {
  const now = new Date();
  const firstDayOfMonth = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    1
  ));
  
  return {
    start_date: formatDateUTC(firstDayOfMonth),
    end_date: formatDateUTC(now)
  };
}

/**
 * Get the full previous month as a date range
 * @returns DateRange for last month
 */
export function getLastMonth(): DateRange {
  const now = new Date();
  
  // Get first day of previous month
  const firstDayOfLastMonth = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth() - 1,
    1
  ));
  
  // Get last day of previous month (day 0 of current month)
  const lastDayOfLastMonth = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    0
  ));
  
  return {
    start_date: formatDateUTC(firstDayOfLastMonth),
    end_date: formatDateUTC(lastDayOfLastMonth)
  };
}

/**
 * Get Quarter-to-Date range (first day of current quarter through today)
 * @returns DateRange for quarter-to-date
 */
export function getQuarterToDate(): DateRange {
  const now = new Date();
  const currentMonth = now.getUTCMonth();
  
  // Calculate first month of current quarter (0, 3, 6, or 9)
  const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
  
  const firstDayOfQuarter = new Date(Date.UTC(
    now.getUTCFullYear(),
    quarterStartMonth,
    1
  ));
  
  return {
    start_date: formatDateUTC(firstDayOfQuarter),
    end_date: formatDateUTC(now)
  };
}

/**
 * Get Year-to-Date range (January 1 of current year through today)
 * @returns DateRange for year-to-date
 */
export function getYearToDate(): DateRange {
  const now = new Date();
  const firstDayOfYear = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
  
  return {
    start_date: formatDateUTC(firstDayOfYear),
    end_date: formatDateUTC(now)
  };
}

/**
 * All available preset date ranges with their configurations
 */
export const DATE_RANGE_PRESETS: RangePreset[] = [
  {
    type: 'today',
    label: 'Today',
    shortLabel: 'Today',
    calculate: getToday
  },
  // {
  //   type: 'yesterday',
  //   label: 'Yesterday',
  //   shortLabel: 'Yday',
  //   calculate: getYesterday
  // },
  {
    type: 'last7days',
    label: 'Last 7 Days',
    shortLabel: '7D',
    calculate: getLast7Days
  },
  {
    type: 'last30days',
    label: 'Last 30 Days',
    shortLabel: '30D',
    calculate: getLast30Days
  },
  {
    type: 'last60days',
    label: 'Last 60 Days',
    shortLabel: '60D',
    calculate: getLast60Days
  },
  {
    type: 'last90days',
    label: 'Last 90 Days',
    shortLabel: '90D',
    calculate: getLast90Days
  },
  {
    type: 'mtd',
    label: 'Month to Date',
    shortLabel: 'MTD',
    calculate: getMonthToDate
  },
  {
    type: 'lastmonth',
    label: 'Last Month',
    shortLabel: 'LM',
    calculate: getLastMonth
  },
  // {
  //   type: 'qtd',
  //   label: 'Quarter to Date',
  //   shortLabel: 'QTD',
  //   calculate: getQuarterToDate
  // },
  {
    type: 'ytd',
    label: 'Year to Date',
    shortLabel: 'YTD',
    calculate: getYearToDate
  }
];

/**
 * Get a preset date range by type
 * @param type - The preset type to calculate
 * @returns DateRange for the specified preset, or today if invalid type
 */
export function getPresetRange(type: RangeType): DateRange {
  if (type === 'custom') {
    return getToday(); // Default to today for custom
  }
  
  const preset = DATE_RANGE_PRESETS.find(p => p.type === type);
  return preset ? preset.calculate() : getToday();
}

/**
 * Validate that a date string is in YYYY-MM-DD format and is a valid date
 * @param dateStr - Date string to validate
 * @returns true if valid, false otherwise
 */
export function isValidDateString(dateStr: string): boolean {
  // Check format YYYY-MM-DD
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateStr)) {
    return false;
  }
  
  // Parse the components
  const [yearStr, monthStr, dayStr] = dateStr.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);
  
  // Check if it's a valid date by verifying the components
  const date = new Date(dateStr + 'T00:00:00Z');
  
  // Verify that the date components match what we put in
  // This catches invalid dates like 2025-02-30 which JS might parse as 2025-03-02
  return !isNaN(date.getTime()) &&
         date.getUTCFullYear() === year &&
         date.getUTCMonth() + 1 === month &&
         date.getUTCDate() === day;
}

/**
 * Validate a date range
 * @param range - DateRange to validate
 * @returns Object with isValid flag and optional error message
 */
export function validateDateRange(range: DateRange): { isValid: boolean; error?: string } {
  // Check date format
  if (!isValidDateString(range.start_date)) {
    return { isValid: false, error: 'Invalid start date format' };
  }
  if (!isValidDateString(range.end_date)) {
    return { isValid: false, error: 'Invalid end date format' };
  }
  
  // Check that end date is not before start date
  if (range.end_date < range.start_date) {
    return { isValid: false, error: 'End date cannot be before start date' };
  }
  
  // Check that dates are not in the future
  const today = formatDateUTC(new Date());
  if (range.start_date > today || range.end_date > today) {
    return { isValid: false, error: 'Dates cannot be in the future' };
  }
  
  // Check maximum range (2 years = 730 days)
  const startDate = new Date(range.start_date + 'T00:00:00Z');
  const endDate = new Date(range.end_date + 'T00:00:00Z');
  const diffDays = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays > 730) {
    return { isValid: false, error: 'Date range cannot exceed 2 years' };
  }
  
  return { isValid: true };
}

/**
 * Format a date range for display
 * @param range - DateRange to format
 * @param preset - Optional preset type to show label instead of dates
 * @returns Formatted string for display
 */
export function formatDateRangeDisplay(range: DateRange, preset?: RangeType): string {
  // If it's a preset, use the label
  if (preset && preset !== 'custom') {
    const presetConfig = DATE_RANGE_PRESETS.find(p => p.type === preset);
    if (presetConfig) {
      return presetConfig.label;
    }
  }
  
  // If it's a single day
  if (range.start_date === range.end_date) {
    const date = new Date(range.start_date + 'T00:00:00Z');
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric',
      timeZone: 'UTC'
    });
  }
  
  // If it's a range
  const startDate = new Date(range.start_date + 'T00:00:00Z');
  const endDate = new Date(range.end_date + 'T00:00:00Z');
  
  const formatOptions: Intl.DateTimeFormatOptions = { 
    month: 'short', 
    day: 'numeric',
    timeZone: 'UTC'
  };
  
  // Add year if dates are in different years or not current year
  const currentYear = new Date().getUTCFullYear();
  const startYear = startDate.getUTCFullYear();
  const endYear = endDate.getUTCFullYear();
  
  const startStr = startDate.toLocaleDateString('en-US', {
    ...formatOptions,
    year: startYear !== currentYear || startYear !== endYear ? 'numeric' : undefined
  });
  
  const endStr = endDate.toLocaleDateString('en-US', {
    ...formatOptions,
    year: 'numeric'
  });
  
  return `${startStr} - ${endStr}`;
}